import { Box } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { PointOfInterestType, PTOPProjectPOIDto } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import { zoomOnFeature } from "~/pages/MapPage/functions/zoomOnFeature";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { usePoiItems } from "../hooks/usePoiItems";
import { PoiItemScreenEnum } from "./PoiItemsContainer";

export type PoiItemTableProps = {
  id: number;
  setActiveScreen: React.Dispatch<React.SetStateAction<PoiItemScreenEnum>>;
  map: Map | undefined;
};

export const PoiItemTable = ({
  id,
  setActiveScreen,
  map,
}: PoiItemTableProps) => {
  const { poiItems, deletePoiItem } = usePoiItems({ id });
  const { t } = useTranslation("projects");
  const dateFormatter = useDateFormatter();
  const handleApiError = useApiErrorHandler();
  const { confirm } = usePopup();
  const columns = React.useMemo(
    (): CustomColumn<PTOPProjectPOIDto>[] => [
      {
        field: "type",
        title: t("projects:typeName"),
        width: "auto",
        render: ({ type }) => t(`poi-item-enum:${PointOfInterestType[type]}`),
      },
      {
        field: "description",
        title: t("projects:description"),
        width: "auto",
      },
      {
        field: "coordinates.x",
        title: "Euref X",
        width: "auto",
      },
      {
        field: "coordinates.y",
        title: "Euref Y",
        width: "auto",
      },
      {
        field: "date",
        title: t("timestamp"),
        width: "auto",
        render: ({ date }) => (date ? dateFormatter({ date }) : ""),
      },
    ],
    [dateFormatter, t],
  );

  const tableProps = React.useMemo(
    (): CustomTableProps<PTOPProjectPOIDto> => ({
      title: t("projects:poiItems"),
      options: {
        filtering: false,
        paging: false,
        search: false,
      },
      actions: [
        {
          icon: "visibility",
          tooltip: t("general:view"),

          onClick: (_e, poiItem) => {
            if (Array.isArray(poiItem) || !map) return;
            const currentZoom = map.getView().getZoom();
            zoomOnFeature(
              map,
              `poi-item-${poiItem.id}`,
              currentZoom && currentZoom > 11 ? currentZoom : 14,
            );
          },
        },
        {
          icon: "delete",
          tooltip: t("general:delete"),
          onClick: (_e, d) => {
            if (Array.isArray(d) || !d.id) return;
            confirm({
              title: t("general:are-you-sure"),
              description: t("general:delete-with-name", {
                name: `${PointOfInterestType[d.type]}`,
              }),
            })
              .then(() =>
                deletePoiItem({ poiItemId: d.id as number, projectId: id }),
              )
              .catch(handleApiError)
              .catch(() => {});
          },
        },

        {
          icon: "add",
          tooltip: t("general:add"),
          isFreeAction: true,
          onClick: () => setActiveScreen(PoiItemScreenEnum.PoiAdd),
        },
      ],
    }),
    [confirm, deletePoiItem, handleApiError, id, map, setActiveScreen, t],
  );
  return (
    <Box
      sx={{
        "& div": {
          boxShadow: "none",
        },
      }}
    >
      <GenericTable<PTOPProjectPOIDto>
        columns={columns}
        data={poiItems ?? []}
        tableProps={tableProps}
        showCopyButton
      />
    </Box>
  );
};
